import type { SessionUser } from '@tandemone/types'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useMemo, useReducer } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { extractErrorMessage } from '@/api/extract-error'
import {
  authClient,
  CAPTCHA_FAILED,
  captchaHeaders,
  ME_KEY
} from '@/domains/auth'
import { createCompany } from '@/domains/onboarding'
import { slugify } from '@/lib/slug'

import {
  type Action,
  type CompanyType,
  type FormState,
  SignupContext
} from './context'
import { companyTypeForPlan, parsePlanParam } from './plan-intent'
import { validatePassword } from './utils'

function splitName(name: string | null | undefined): {
  first: string
  last: string
} {
  if (!name) return { first: '', last: '' }
  const parts = name.trim().split(/\s+/)
  return { first: parts[0] ?? '', last: parts.slice(1).join(' ') }
}

function initialState(
  googleUser: SessionUser | null,
  companyType: CompanyType | null
): FormState {
  const { first, last } = splitName(googleUser?.name)
  return {
    companyName: '',
    companyType,
    firstName: first,
    lastName: last,
    email: googleUser?.email ?? '',
    password: '',
    confirmPassword: '',
    error: '',
    submitting: false
  }
}

function formReducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    case 'SET_COMPANY_NAME':
      return { ...state, companyName: action.payload }
    case 'SET_COMPANY_TYPE':
      return { ...state, companyType: action.payload }
    case 'SET_FIRST_NAME':
      return { ...state, firstName: action.payload }
    case 'SET_LAST_NAME':
      return { ...state, lastName: action.payload }
    case 'SET_EMAIL':
      return { ...state, email: action.payload }
    case 'SET_PASSWORD':
      return { ...state, password: action.payload }
    case 'SET_CONFIRM_PASSWORD':
      return { ...state, confirmPassword: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload, submitting: false }
    case 'CLEAR_ERROR':
      return { ...state, error: '' }
    case 'SUBMIT_START':
      return { ...state, submitting: true, error: '' }
    default:
      return state
  }
}

export function SignupFormProvider({
  children,
  googleUser = null,
  onSuccess
}: {
  children: ReactNode
  googleUser?: SessionUser | null
  onSuccess: () => void
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const next = searchParams.get('next')
  const plan = parsePlanParam(searchParams.get('plan'))
  const [state, dispatch] = useReducer(
    formReducer,
    initialState(googleUser, companyTypeForPlan(plan))
  )

  const slug = useMemo(() => slugify(state.companyName), [state.companyName])

  const isValid = googleUser
    ? state.companyName.trim().length > 0 &&
      slug.length >= 2 &&
      state.companyType !== null
    : state.firstName.length > 0 &&
      state.lastName.length > 0 &&
      state.email.length > 0 &&
      state.password.length > 0 &&
      state.confirmPassword.length > 0

  async function handleSubmit(event: React.SyntheticEvent) {
    event.preventDefault()
    dispatch({ type: 'CLEAR_ERROR' })

    if (googleUser) {
      // google verified the email already. just attach the company
      if (!state.companyType) return
      dispatch({ type: 'SUBMIT_START' })
      try {
        await createCompany({
          name: state.companyName.trim(),
          slug,
          type: state.companyType
        })
      } catch (companyFailure) {
        dispatch({
          type: 'SET_ERROR',
          payload: extractErrorMessage(companyFailure)
        })
        return
      }
      await queryClient.invalidateQueries({ queryKey: ME_KEY })
      await queryClient.invalidateQueries({ queryKey: ['setup'] })
      onSuccess()
      return
    }

    const passwordError = validatePassword(state.password)
    if (passwordError) {
      dispatch({ type: 'SET_ERROR', payload: passwordError })
      return
    }
    if (state.password !== state.confirmPassword) {
      dispatch({ type: 'SET_ERROR', payload: 'Passwords do not match.' })
      return
    }

    dispatch({ type: 'SUBMIT_START' })
    const captcha = await captchaHeaders()
    if (!captcha) {
      dispatch({ type: 'SET_ERROR', payload: CAPTCHA_FAILED })
      return
    }
    /** with requireEmailVerification: true, signUp.email creates the user and
     * sends a verification code, but doesn't sign in. the user enters the code
     * on /verify, which signs them in (autoSignInAfterVerification); ?next=
     * rides in history state, not a callbackURL, since the OTP flow has no
     * email link to redirect through.
     */
    const { error } = await authClient.signUp.email(
      {
        email: state.email,
        password: state.password,
        name: `${state.firstName} ${state.lastName}`.trim()
      },
      captcha
    )

    if (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error.message ?? 'Something went wrong. Please try again.'
      })
      return
    }

    /** hop to the code-entry view. email + next are passed via history state
     * so they don't end up in the URL bar / page-view events / axiom logs.
     * tradeoff: hard refresh on /verify loses the state and bounces back to
     * /signup.
     */
    void navigate('/verify', {
      replace: true,
      state: {
        email: state.email,
        next: next ?? undefined,
        plan: plan ?? undefined
      }
    })
  }

  return (
    <SignupContext.Provider
      value={{ state, dispatch, googleUser, slug, isValid, handleSubmit }}
    >
      {children}
    </SignupContext.Provider>
  )
}
